// Virtual folder buckets for the vault browse UI (custom folders are client-side for now).
import type { MessageKey } from "~/i18n";
import type { Translator } from "~/i18n";
import type { VaultItem } from "../../morrowVault";
import {
  isAudioMime,
  isImageMime,
  isOfficeFile,
  isPdfMime,
  isSpreadsheetFile,
  isStructuredTextMime,
  isTextMime,
  isVideoMime,
  resolveVaultMimeType,
} from "./vaultShared";

export type SystemVaultFolderId =
  | "all"
  | "documents"
  | "spreadsheets"
  | "images"
  | "media"
  | "other";

export type VaultFolderId = SystemVaultFolderId | `custom:${string}`;

export type CustomVaultFolder = {
  readonly id: string;
  readonly name: string;
  readonly createdAt: string;
};

export type VaultFolderDefinition = {
  readonly id: VaultFolderId;
  readonly labelKey?: MessageKey;
  readonly customName?: string;
};

export const VAULT_FOLDERS: readonly VaultFolderDefinition[] = [
  { id: "all", labelKey: "vault.folder.all" },
  { id: "documents", labelKey: "vault.folder.documents" },
  { id: "spreadsheets", labelKey: "vault.folder.spreadsheets" },
  { id: "images", labelKey: "vault.folder.images" },
  { id: "media", labelKey: "vault.folder.media" },
  { id: "other", labelKey: "vault.folder.other" },
] as const;

const SYSTEM_FOLDER_IDS = VAULT_FOLDERS.map((folder) => folder.id);

function emptySystemGroups(): Record<SystemVaultFolderId, VaultItem[]> {
  return {
    all: [],
    documents: [],
    spreadsheets: [],
    images: [],
    media: [],
    other: [],
  };
}

export function customVaultFolderId(id: string): `custom:${string}` {
  return `custom:${id}`;
}

export function isCustomVaultFolderId(id: string): id is `custom:${string}` {
  return id.startsWith("custom:");
}

export function customFolderRawId(folderId: VaultFolderId): string | null {
  if (!isCustomVaultFolderId(folderId)) return null;
  return folderId.slice("custom:".length);
}

export function folderDisplayLabel(folder: VaultFolderDefinition, t: Translator): string {
  if (folder.customName) return folder.customName;
  if (folder.labelKey) return t(folder.labelKey);
  return folder.id;
}

/** All browse folders: system buckets plus user-created folders. */
export function browseVaultFolders(
  customFolders: readonly CustomVaultFolder[],
): VaultFolderDefinition[] {
  return [
    ...VAULT_FOLDERS,
    ...customFolders.map((folder) => ({
      id: customVaultFolderId(folder.id),
      customName: folder.name,
    })),
  ];
}

function isDocumentFile(item: VaultItem): boolean {
  const mime = resolveVaultMimeType(item);
  return (
    isPdfMime(mime) ||
    isTextMime(mime) ||
    isStructuredTextMime(mime) ||
    isOfficeFile(item) ||
    mime.includes("wordprocessing") ||
    mime === "application/msword" ||
    item.hasText
  );
}

function isMediaFile(item: VaultItem): boolean {
  const mime = resolveVaultMimeType(item);
  return isVideoMime(mime) || isAudioMime(mime);
}

function isImageFile(item: VaultItem): boolean {
  return isImageMime(resolveVaultMimeType(item));
}

/** Assign a vault item to exactly one system browse folder (excluding "all"). */
export function vaultFolderForItem(item: VaultItem): Exclude<SystemVaultFolderId, "all"> {
  if (isImageFile(item)) return "images";
  if (isMediaFile(item)) return "media";
  if (isSpreadsheetFile(item)) return "spreadsheets";
  if (isDocumentFile(item)) return "documents";
  return "other";
}

export function groupVaultItemsByFolder(
  items: readonly VaultItem[],
  customFolders: readonly CustomVaultFolder[] = [],
  assignments: Readonly<Record<string, string>> = {},
): Record<VaultFolderId, VaultItem[]> {
  const groups: Record<VaultFolderId, VaultItem[]> = {
    ...emptySystemGroups(),
    all: [...items],
  };

  for (const folder of customFolders) {
    groups[customVaultFolderId(folder.id)] = [];
  }

  for (const item of items) {
    const assignedFolderId = assignments[item.id];
    if (assignedFolderId) {
      const customKey = customVaultFolderId(assignedFolderId);
      if (groups[customKey]) {
        groups[customKey].push(item);
      }
      continue;
    }

    const folder = vaultFolderForItem(item);
    groups[folder].push(item);
  }

  return groups;
}

export function isVaultFolderId(value: string): value is VaultFolderId {
  if ((SYSTEM_FOLDER_IDS as readonly string[]).includes(value)) return true;
  return isCustomVaultFolderId(value);
}

/** @deprecated Use browseVaultFolders instead. */
export function visibleVaultFolders(
  groups: Record<VaultFolderId, VaultItem[]>,
): VaultFolderDefinition[] {
  return VAULT_FOLDERS.filter(
    (folder) => folder.id === "all" || (groups[folder.id]?.length ?? 0) > 0,
  );
}
